/**
 * Dark Pool Auction Configuration
 * 3 coins per round, player bets on which will pump.
 * Ad reveals "insider info" (~80% accuracy).
 * Coin icons are initial circles (color + 2 letters) - no emoji.
 */
import { COINS, getCoinConfig, type CoinConfig } from './coins.js';

export interface DarkPoolCoinConfig {
  id: string;
  name: string;
  /** initial-circle background color (CSS hex) */
  color: string;
}

/** Pool of fictional coins for rounds */
export const DARKPOOL_COINS: DarkPoolCoinConfig[] = [
  { id: 'dp_moon', name: 'MOONSHOT', color: '#8FA8FF' },
  { id: 'dp_frog', name: 'FROGCOIN', color: '#5CC85C' },
  { id: 'dp_pizza', name: 'PIZZASWAP', color: '#F2994A' },
  { id: 'dp_cat', name: 'CATCHAIN', color: '#E0A96D' },
  { id: 'dp_rock', name: 'ROCKSOLID', color: '#9AA5B1' },
  { id: 'dp_yolo', name: 'YOLOCOIN', color: '#FF6B6B' },
  { id: 'dp_ramen', name: 'RAMENFI', color: '#F5D061' },
  { id: 'dp_ghost', name: 'GHOSTNET', color: '#C8BFE7' },
  { id: 'dp_crab', name: 'CRABDAO', color: '#FF8A5C' },
  { id: 'dp_taco', name: 'TACOTOKEN', color: '#E9C46A' },
  { id: 'dp_sock', name: 'SOCKSWAP', color: '#6FD6C2' },
  { id: 'dp_ufo', name: 'UFOMONEY', color: '#7FE7F5' },
];

/** Round-coin id prefix marking a real exchange meme coin */
export const DP_EXCHANGE_PREFIX = 'ex:';

/** Exchange coin id behind a dark pool round coin (null for pool coins) */
export function dpExchangeCoinId(dpCoinId: string | undefined | null): string | null {
  if (!dpCoinId || !dpCoinId.startsWith(DP_EXCHANGE_PREFIX)) return null;
  const coinId = dpCoinId.slice(DP_EXCHANGE_PREFIX.length);
  return getCoinConfig(coinId) ? coinId : null;
}

function toDarkPoolCoin(cfg: CoinConfig): DarkPoolCoinConfig {
  return { id: DP_EXCHANGE_PREFIX + cfg.id, name: cfg.symbol, color: cfg.color };
}

/** Multiplier options for winning coin */
export const DARKPOOL_MULTIPLIERS = [2, 3, 5, 8, 12] as const;

export const DARKPOOL_CONFIG = {
  /** Number of coins per round */
  coinsPerRound: 3,

  /** Round phases duration */
  bettingDuration: 20_000,    // 20s to place bets
  revealDuration: 5_000,      // 5s dramatic reveal animation
  resultDuration: 8_000,      // 8s to see result & claim

  /** Cooldown between rounds */
  roundCooldown: 120_000,     // 2min between rounds

  /** Bet limits (as % of current coins) */
  minBetRatio: 0.01,          // min 1% of coins
  maxBetRatio: 0.25,          // max 25% of coins

  /** Insider info accuracy (ad reward) */
  insiderAccuracy: 0.8,       // 80% chance the tip is correct

  /** Exchange integration (Sprint 2b): one slot may host a real meme coin */
  exchangeCoinChance: 0.25,   // 25% of rounds
  exchangePushImpact: 2.5,    // momentum push (volatility units) when it wins
  exchangePushDuration: 60_000, // exchange price push lasts 60s

  /** Multiplier distribution weights (lower mult = more likely) */
  multiplierWeights: {
    2: 40,    // 40% chance
    3: 30,    // 30% chance
    5: 18,    // 18% chance
    8: 9,     // 9% chance
    12: 3,    // 3% chance
  } as Record<number, number>,

  /** Win probability per coin (each coin has equal chance) */
  // With 3 coins: ~33% base win chance
} as const;

/**
 * Get reward multiplier from upgrades
 * up_dp_scam_promo: winnings x2
 */
export function getRewardMultiplier(purchasedUpgrades?: string[]): number {
  if (purchasedUpgrades?.includes('up_dp_scam_promo')) return 2;
  return 1;
}

/**
 * Get max bet ratio (with upgrades)
 * up_dp_chip_laundry: max bet 25% → 50%
 */
export function getMaxBetRatio(purchasedUpgrades?: string[]): number {
  if (purchasedUpgrades?.includes('up_dp_chip_laundry')) return 0.5;
  return DARKPOOL_CONFIG.maxBetRatio;
}

/**
 * Get insider accuracy (with upgrades)
 */
export function getInsiderAccuracy(purchasedUpgrades?: string[]): number {
  // up_dp_insider_net: 80% → 95%
  if (purchasedUpgrades?.includes('up_dp_insider_net')) return 0.95;
  return DARKPOOL_CONFIG.insiderAccuracy;
}

/**
 * Pick N random coins from pool.
 * With `exchangeChance` one slot hosts a random exchange meme coin -
 * if it wins the round, the exchange price gets a 60s push.
 */
export function pickRoundCoins(
  n: number,
  listedMemeIds?: string[],
  exchangeChance: number = DARKPOOL_CONFIG.exchangeCoinChance
): DarkPoolCoinConfig[] {
  const shuffled = [...DARKPOOL_COINS].sort(() => Math.random() - 0.5);
  const picked = shuffled.slice(0, n);
  // Only listed meme coins can host a real exchange slot (delisted have no market).
  const memes = (listedMemeIds ?? COINS.filter(c => c.tier === 'meme').map(c => c.id))
    .map(id => getCoinConfig(id))
    .filter((c): c is CoinConfig => !!c && c.tier === 'meme');
  if (memes.length > 0 && n > 0 && Math.random() < exchangeChance) {
    const meme = memes[Math.floor(Math.random() * memes.length)];
    picked[Math.floor(Math.random() * picked.length)] = toDarkPoolCoin(meme);
  }
  return picked;
}

/**
 * Determine winning coin and multiplier
 */
export function resolveRound(coinIds: string[], purchasedUpgrades?: string[]): { winnerId: string; multiplier: number } {
  // Random winner
  const winnerId = coinIds[Math.floor(Math.random() * coinIds.length)];

  // Weighted random multiplier (고래 조종: boost 8x/12x chances)
  const weights = { ...DARKPOOL_CONFIG.multiplierWeights };
  if (purchasedUpgrades?.includes('up_dp_whale_ctrl')) {
    weights[8] = 25;   // 9% → 25%
    weights[12] = 15;  // 3% → 15%
  }
  const entries = Object.entries(weights).map(([m, w]) => ({ mult: Number(m), weight: w }));
  const totalWeight = entries.reduce((sum, e) => sum + e.weight, 0);
  let roll = Math.random() * totalWeight;
  let multiplier = 2;
  for (const entry of entries) {
    roll -= entry.weight;
    if (roll <= 0) {
      multiplier = entry.mult;
      break;
    }
  }

  return { winnerId, multiplier };
}

/**
 * Generate insider info (may be wrong ~20% of time)
 */
export function generateInsiderInfo(
  coinIds: string[],
  actualWinnerId: string,
  actualMultiplier: number,
  purchasedUpgrades?: string[]
): { coinId: string; hintMultiplier: number; isAccurate: boolean } {
  const accuracy = getInsiderAccuracy(purchasedUpgrades);
  const isAccurate = Math.random() < accuracy;

  if (isAccurate) {
    return {
      coinId: actualWinnerId,
      hintMultiplier: actualMultiplier,
      isAccurate: true,
    };
  } else {
    // Give wrong info — pick a different coin
    const wrongCoins = coinIds.filter(id => id !== actualWinnerId);
    const wrongCoinId = wrongCoins[Math.floor(Math.random() * wrongCoins.length)];
    // Give a plausible but wrong multiplier
    const fakeMult = DARKPOOL_MULTIPLIERS[Math.floor(Math.random() * DARKPOOL_MULTIPLIERS.length)];
    return {
      coinId: wrongCoinId,
      hintMultiplier: fakeMult,
      isAccurate: false,
    };
  }
}
